import React, { useMemo, useState } from 'react';

import { link } from '@aline/routes';
import { LinearProgress, Popover, Skeleton, Typography, useTheme } from '@mui/material';
import { matchSorter } from 'match-sorter';
import { useNavigate } from 'react-router-dom';
import { AlineButton, AlineTooltip, Spacer } from 'src/components';
import {
  getIsJobSyncing,
  getJobTotalFileIds,
} from 'src/components/admin/integrations/FolderSyncJobsList';
import {
  getServiceIdFromIntegration,
  IntegrationDisplay,
} from 'src/components/admin/integrations/IntegrationRow';
import { SyncingIndicator } from 'src/components/admin/integrations/SyncingIndicator';
import { useFolderSyncJobs } from 'src/components/admin/integrations/useFolderSyncJobs';
import { ErrorIcon } from 'src/components/icons/ErrorIcon';
import { SyncIcon } from 'src/components/icons/SyncIcon';
import { SearchInput } from 'src/components/SearchInput';
import { Apollo } from 'src/gql/generated';
import { useFileStorageConnections, useOnFolderSyncJobUpdateSubscription } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { getEditorPopoverStyles, getEditorSearchStyles } from './utils';
import { usePopoverDimensions } from '../../chat-hooks/usePopoverPosition';

const useStyles = makeStyles()(theme => {
  const sharedSearchStyles = getEditorSearchStyles(theme);
  const sharedPopoverStyles = getEditorPopoverStyles(theme);
  return {
    ...sharedSearchStyles,
    clickableIntegrationRow: {
      '&:hover': {
        backgroundColor: theme.appColors.background,
      },
      alignItems: 'center',
      border: `1px dashed ${theme.appColors.rule}`,
      borderRadius: 8,
      cursor: 'pointer',
      display: 'flex',
      gap: theme.spacing(1),
      padding: theme.spacing(1, 1.5),
    },
    failedFilesBadge: {
      backgroundColor: theme.palette.error.main,
      borderRadius: 4,
      color: theme.palette.common.white,
      cursor: 'pointer',
      fontSize: 10,
      fontWeight: 600,
      padding: theme.spacing(0.25, 0.75),
    },
    header: {
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(1),
    },
    headerTitle: {
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    icon: {
      display: 'block',
      height: 30,
      width: 30,
    },
    integrationRow: {
      alignItems: 'center',
      border: `1px dashed ${theme.appColors.rule}`,
      borderRadius: 8,
      display: 'flex',
      gap: theme.spacing(1.5),
      padding: theme.spacing(1, 1.5),
    },
    listContainer: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      gap: theme.spacing(1),
      overflow: 'auto',
    },
    noResults: {
      alignItems: 'center',
      color: theme.palette.text.secondary,
      display: 'flex',
      flex: 1,
      justifyContent: 'center',
    },
    popoverPaper: {
      ...sharedPopoverStyles.popoverPaper,
      // Width is set dynamically via inline style based on available space
    },
    progressBar: {
      borderRadius: 4,
      height: 4,
      width: 80,
    },
    statusContainer: {
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(0.5),
    },
    statusText: {
      fontSize: 10,
    },
    syncButton: {
      minWidth: 80,
    },
    syncedText: {
      fontSize: 12,
    },
    syncingStatus: {
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(1),
    },
    syncingText: {
      fontSize: 12,
      whiteSpace: 'nowrap',
    },
  };
});

export const autoSyncParamKey = 'autoSync';

export interface ChatEditorIntegrationsProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  open: boolean;
}

export function ChatEditorIntegrations({
  anchorEl,
  onClose,
  open,
}: ChatEditorIntegrationsProps): JSX.Element {
  const theme = useTheme();
  const { classes } = useStyles();
  const marginThresholdPx = 8;
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  // Use skip: !open to ensure refetch of data when the popover is opened.
  const { fileStorageConnections, loading } = useFileStorageConnections({ skip: !open });
  const { maxHeight, openAbove, width } = usePopoverDimensions(anchorEl, {
    marginThresholdPx,
  });

  // Subscribe to real-time sync job updates.
  useOnFolderSyncJobUpdateSubscription();

  // Sort integrations: connected first, then not connected.
  const sortedAndFilteredIntegrations = useMemo(() => {
    const integrations = searchQuery.trim()
      ? matchSorter(fileStorageConnections, searchQuery, {
          keys: ['name'] satisfies Array<keyof (typeof fileStorageConnections)[number]>,
        })
      : fileStorageConnections;

    // Sort: connected integrations first.
    return [...integrations].sort((a, b) => {
      const aConnected = a.state === Apollo.ApiDeckConnectionState.Callable ? 0 : 1;
      const bConnected = b.state === Apollo.ApiDeckConnectionState.Callable ? 0 : 1;
      return aConnected - bConnected;
    });
  }, [fileStorageConnections, searchQuery]);

  const handleSyncClick = (integration: Apollo.FileStorageIntegration) => {
    onClose();
    navigate(
      `${link.AdminIntegrations()}?${autoSyncParamKey}=${getServiceIdFromIntegration(integration)}`
    );
  };

  return (
    <Popover
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
      anchorEl={anchorEl}
      anchorOrigin={{
        horizontal: 'center',
        vertical: openAbove ? 'top' : 'bottom',
      }}
      classes={{ paper: classes.popoverPaper }}
      marginThreshold={marginThresholdPx}
      open={open}
      transformOrigin={{
        horizontal: 'center',
        vertical: openAbove ? 'bottom' : 'top',
      }}
      onClick={e => e.stopPropagation()}
      onClose={onClose}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(1.5),
          maxHeight,
          padding: theme.spacing(1, 2),
          width,
        }}
      >
        {/* Header with title */}
        <div className={classes.header}>
          <Typography
            className={classes.headerTitle}
            sx={{
              fontWeight: 'bold',
            }}
            variant="body1"
          >
            Integrations
          </Typography>
        </div>

        {/* Search input */}
        <SearchInput
          autoFocus
          inputClassName={classes.searchInput}
          minWidth={0}
          placeholder="Search for an integration..."
          search={searchQuery}
          setSearch={setSearchQuery}
        />

        {/* Integration list */}
        <ChatIntegrationsBody
          integrations={sortedAndFilteredIntegrations}
          loading={loading}
          onSyncClick={handleSyncClick}
        />
      </div>
    </Popover>
  );
}

function ChatIntegrationsBody({
  integrations,
  loading,
  onSyncClick,
}: {
  integrations: Apollo.FileStorageConnection[];
  loading: boolean;
  onSyncClick: (integration: Apollo.FileStorageIntegration) => void;
}): JSX.Element {
  const { classes } = useStyles();

  if (loading) {
    return (
      <div className={classes.listContainer}>
        {[1, 2, 3].map(i => (
          <div key={i} className={classes.integrationRow}>
            <Skeleton height={32} variant="rectangular" width={32} />
            <Skeleton height={20} variant="text" width="40%" />
            <Spacer />
            <Skeleton height={32} variant="rectangular" width={80} />
          </div>
        ))}
      </div>
    );
  }

  if (!integrations.length) {
    return (
      <div className={classes.listContainer}>
        <Typography className={classes.noResults} variant="body2">
          No integrations found
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.listContainer}>
      {integrations.map(integration => (
        <IntegrationRowWithStatus
          key={integration.id}
          integration={integration}
          onSyncClick={() => onSyncClick(integration.serviceId)}
        />
      ))}
    </div>
  );
}

interface IntegrationRowWithStatusProps {
  integration: Apollo.FileStorageConnection;
  onSyncClick: () => void;
}

function IntegrationRowWithStatus({
  integration,
  onSyncClick,
}: IntegrationRowWithStatusProps): JSX.Element {
  const isConnected = integration.state === Apollo.ApiDeckConnectionState.Callable;

  // Only fetch sync jobs for connected integrations
  if (!isConnected) {
    return (
      <ClickableIntegrationRow integration={integration} onClick={onSyncClick}>
        <SyncButton
          onClick={e => {
            e.stopPropagation();
            onSyncClick();
          }}
        />
      </ClickableIntegrationRow>
    );
  }

  return <ConnectedIntegrationRow integration={integration} />;
}

interface ConnectedIntegrationRowProps {
  integration: Apollo.FileStorageConnection;
}

function ConnectedIntegrationRow({ integration }: ConnectedIntegrationRowProps): JSX.Element {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { grouped, hasValues, loading } = useFolderSyncJobs(integration.serviceId);

  // Calculate sync status across all jobs for this integration
  const syncStatus = useMemo(() => {
    const stateObj: {
      failedFilesCount: number;
      isAnySyncing: boolean;
      syncedFiles: number;
      totalFiles: number;
    } = {
      failedFilesCount: 0,
      isAnySyncing: false,
      syncedFiles: 0,
      totalFiles: 0,
    };

    if (loading || !hasValues) return stateObj;

    Object.values(grouped).forEach(({ jobs }) => {
      jobs.forEach(job => {
        const activeFailedFiles = job.failedFiles.filter(f => !f.isDimissed);
        const jobTotalFilesCount = getJobTotalFileIds(job).length;

        stateObj.totalFiles += jobTotalFilesCount;
        stateObj.syncedFiles += job.completedIds.length;
        stateObj.failedFilesCount += activeFailedFiles.length;

        if (getIsJobSyncing(job)) stateObj.isAnySyncing = true;
      });
    });

    return stateObj;
  }, [grouped, hasValues, loading]);

  const { failedFilesCount, isAnySyncing, syncedFiles, totalFiles } = syncStatus;
  const progressPercentage = totalFiles > 0 ? (syncedFiles / totalFiles) * 100 : 100;

  const handleViewFailedFiles = () => {
    navigate(`${link.AdminIntegrations()}?${autoSyncParamKey}=${integration.serviceId}`);
  };

  return (
    <ClickableIntegrationRow
      integration={integration}
      statusIndicator={isAnySyncing ? <SyncingIndicator /> : undefined}
      onClick={handleViewFailedFiles}
    >
      {failedFilesCount > 0 && (
        <AlineTooltip arrow placement="top" title={`${failedFilesCount} files failed to sync`}>
          <ErrorIcon style={{ height: 20, width: 20 }} />
        </AlineTooltip>
      )}
      <div className={classes.syncingStatus}>
        <Typography className={classes.syncingText} color="textSecondary" variant="body2">
          {syncedFiles}/{totalFiles} files synced
        </Typography>
        <LinearProgress
          className={classes.progressBar}
          value={progressPercentage}
          variant="determinate"
        />
      </div>
    </ClickableIntegrationRow>
  );
}

interface ClickableIntegrationRowProps {
  children: React.ReactNode;
  integration: Apollo.FileStorageConnection;
  onClick: () => void;
  /** Status indicator rendered after the integration name */
  statusIndicator?: React.ReactNode;
}

function ClickableIntegrationRow({
  children,
  integration,
  onClick,
  statusIndicator,
}: ClickableIntegrationRowProps): JSX.Element {
  const { classes } = useStyles();
  const isConnected = integration.state === Apollo.ApiDeckConnectionState.Callable;

  return (
    <div
      className={classes.clickableIntegrationRow}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={e => e.key === 'Enter' && onClick()}
    >
      <IntegrationDisplay
        iconClassName={classes.icon}
        imageUrl={integration.imageUrl}
        isConnected={isConnected}
        name={integration.name}
      />
      {statusIndicator}
      <Spacer />
      {children}
    </div>
  );
}

function SyncButton({ onClick }: { onClick: (e: React.MouseEvent) => void }): JSX.Element {
  const theme = useTheme();
  const { classes } = useStyles();

  return (
    <AlineButton
      className={classes.syncButton}
      color="primary"
      size="small"
      variant="outlined"
      onClick={onClick}
    >
      Sync
      <SyncIcon stroke="currentColor" style={{ marginLeft: theme.spacing(0.75) }} />
    </AlineButton>
  );
}
